import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { 
  Map, 
  Users, 
  FileText, 
  Home, 
  TrendingUp, 
  Clock,
  CheckCircle,
  XCircle,
  User
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [floorStats, setFloorStats] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Temel veriler (tüm kullanıcılar için)
      const [statsResponse, floorStatsResponse] = await Promise.all([
        axios.get('/api/rooms'),
        axios.get('/api/rooms/stats/floors')
      ]);

      const roomsData = statsResponse.data;
      setRooms(roomsData);
      
      const totalRooms = roomsData.length;
      const availableRooms = roomsData.filter(r => r.available_beds > 0).length;
      const occupiedRooms = roomsData.filter(r => r.occupied === r.capacity).length;
      const totalStudents = roomsData.reduce((sum, room) => sum + room.occupied, 0);

      setStats({
        totalRooms,
        availableRooms,
        occupiedRooms,
        totalStudents
      });

      setFloorStats(floorStatsResponse.data);

      // Sadece öğrenciler için başvuru verileri
      if (user?.role === 'student') {
        const [applicationsResponse, roomResponse] = await Promise.all([
          axios.get('/api/applications/my-applications'),
          axios.get('/api/applications/my-room')
        ]);

        setApplications(applicationsResponse.data);
        setCurrentRoom(roomResponse.data);
      }
    } catch (error) {
      console.error('Dashboard verileri alınamadı:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'approved':
        return 'Onaylandı';
      case 'rejected':
        return 'Reddedildi';
      default:
        return 'Bilinmiyor';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const hasActiveApplication = applications.some(app => app.status === 'pending');
  const hasRejectedApplication = applications.some(app => app.status === 'rejected');

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto !py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Hoş Geldiniz!
        </h1>
        <p className="text-gray-600">
          Korkmazlar Yurt oda seçim sistemine hoş geldiniz. Aşağıdaki seçeneklerden birini kullanarak işlem yapabilirsiniz.
        </p>
      </div>

      {/* Mevcut Oda Bilgisi - Sadece Öğrenciler */}
          {user?.role === 'student' && currentRoom && (
            <div className="card mb-8 bg-primary-50 border-primary-200">
              <div className="flex items-center">
                <div className="p-3 bg-primary-100 rounded-lg">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div className="ml-4">
                  <div className="text-lg font-semibold text-primary-900">
                    Mevcut Odanız: {currentRoom.room_number}
                  </div>
                  <div className="text-sm text-primary-700">
                    {currentRoom.floor}. Kat • {currentRoom.occupied}/{currentRoom.capacity} kişi
                  </div>
                  <div className="text-xs text-primary-600 mt-1">
                    {new Date(currentRoom.joined_at).toLocaleDateString('tr-TR')} tarihinden beri
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Başvuru Durumu Uyarısı - Sadece Öğrenciler */}
          {user?.role === 'student' && hasActiveApplication && (
            <div className="card mb-8 bg-yellow-50 border-yellow-200">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <div className="text-lg font-semibold text-yellow-900">
                    Bekleyen Başvurunuz Var
                  </div>
                  <div className="text-sm text-yellow-700">
                    Başvurunuz yönetici tarafından inceleniyor. Sonucu bekleyin.
                  </div>
                </div>
              </div>
            </div>
          )}

          {user?.role === 'student' && hasRejectedApplication && !currentRoom && (
            <div className="card mb-8 bg-red-50 border-red-200">
              <div className="flex items-center">
                <div className="p-3 bg-red-100 rounded-lg">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <div className="text-lg font-semibold text-red-900">
                    Başvurunuz Reddedildi
                  </div>
                  <div className="text-sm text-red-700">
                    Yeni bir oda seçimi yapabilirsiniz.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* İstatistikler */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="card">
              <div className="flex items-center">
                <div className="p-3 bg-primary-100 rounded-lg">
                  <Home className="w-6 h-6 text-primary" />
                </div>
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">{stats?.totalRooms}</div>
                  <div className="text-sm text-gray-600">Toplam Oda</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Map className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">{stats?.availableRooms}</div>
                  <div className="text-sm text-gray-600">Müsait Oda</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">{stats?.totalStudents}</div>
                  <div className="text-sm text-gray-600">Toplam Öğrenci</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <div className="text-2xl font-bold text-gray-900">{stats?.occupiedRooms}</div>
                  <div className="text-sm text-gray-600">Dolu Oda</div>
                </div>
              </div>
            </div>
          </div>

          {/* Hızlı Erişim - Tüm Katlar */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Kat Planları</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((floor) => {
                const floorData = floorStats.find(f => f.floor === floor);
                const colors = [
                  'bg-primary-100 text-primary group-hover:bg-primary-200',
                  'bg-green-100 text-green-600 group-hover:bg-green-200',
                  'bg-purple-100 text-purple-600 group-hover:bg-purple-200',
                  'bg-orange-100 text-orange-600 group-hover:bg-orange-200',
                  'bg-pink-100 text-pink-600 group-hover:bg-pink-200',
                  'bg-indigo-100 text-indigo-600 group-hover:bg-indigo-200'
                ];
                
                return (
                  <Link
                    key={floor}
                    to={`/floor/${floor}?tab=rooms`}
                    className="card hover:shadow-lg transition-shadow cursor-pointer group text-center"
                  >
                    <div className="flex flex-col items-center">
                      <div className={`p-3 rounded-lg transition-colors ${colors[floor - 1]}`}>
                        <Map className="w-6 h-6" />
                      </div>
                      <div className="mt-3">
                        <div className="text-lg font-semibold text-gray-900">{floor}. Kat</div>
                        <div className="text-xs text-gray-600">
                          {floorData ? (
                            <>
                              <div>{floorData.total_rooms} Oda</div>
                              <div className="text-green-600">{floorData.available_beds} Boş Yatak</div>
                              <div className="text-gray-500">{floorData.occupancy_rate}% Doluluk</div>
                            </>
                          ) : (
                            <div>10 Oda</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Başvuru Geçmişi - Sadece Öğrenciler */}
          {user?.role === 'student' && (
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Başvuru Geçmişi</h2>
                <Link
                  to="/applications"
                  className="text-primary hover:text-primary-600 text-sm font-medium"
                >
                  Tümünü Gör →
                </Link>
              </div>

              {applications.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <div className="text-lg font-medium text-gray-900 mb-2">
                    Henüz başvurunuz yok
                  </div>
                  <p className="text-gray-600">
                    Oda seçimi yapmak için kat planlarını inceleyebilirsiniz.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {applications.slice(0, 5).map((application) => (
                    <div
                      key={application.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        {getStatusIcon(application.status)}
                        <div>
                          <div className="font-medium text-gray-900">
                            Oda {application.room_number}
                          </div>
                          <div className="text-sm text-gray-600">
                            {new Date(application.created_at).toLocaleDateString('tr-TR')}
                          </div>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                        {getStatusText(application.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
    </div>
  );
};

export default Dashboard; 